import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { categories, getComicsByCategory } from '@/mocks/comics';
import type { BreadcrumbItem } from '@/components/feature/Breadcrumb';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'rexue');

  useEffect(() => {
    if (slug) setActiveSlug(slug);
    window.scrollTo(0, 0);
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeSlug) || categories[0];
  const comics = getComicsByCategory(activeSlug);

  const breadcrumbItems: BreadcrumbItem[] = [
    { name: '首页', path: '/' },
    { name: '漫画分类', path: '/category/rexue' },
    { name: currentCategory.name },
  ];

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name}漫画 - 叮当漫画网`,
    description: currentCategory.description,
    url: `https://dingdangmh.com/category/${activeSlug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '叮当漫画网',
      url: 'https://dingdangmh.com',
    },
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(categorySchema) }}
      />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <Breadcrumb items={breadcrumbItems} />
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6 pb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100">
                <i className={`${currentCategory.icon} text-xl text-primary-500`}></i>
              </div>
              <div>
                <h1 className="text-xl md:text-2xl font-heading text-foreground-900 tracking-wide">
                  {currentCategory.name}漫画
                </h1>
                <p className="text-sm text-foreground-500">{currentCategory.description}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Category tabs */}
        <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-6">
          <div className="flex items-center gap-2 flex-wrap">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeSlug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className={`${cat.icon} text-sm`}></i>
                </span>
                {cat.name}
              </Link>
            ))}
          </div>
        </div>

        {/* Comic grid */}
        <div className="w-full max-w-7xl mx-auto px-4 md:px-6 pb-14 md:pb-18">
          {comics.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
              {comics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} showDescription />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                <i className="ri-inbox-line text-2xl text-foreground-300"></i>
              </div>
              <p className="text-foreground-500">该分类暂无漫画，请查看其他分类</p>
            </div>
          )}

          {comics.length > 0 && (
            <div className="mt-10 text-center">
              <p className="text-sm text-foreground-400">
                共 {comics.length} 部{currentCategory.name}漫画
              </p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}